import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAgent } from '../../../../context/agent/AgentContext';
import { useDashboard } from '../../../../context/dashboard/DashboardContext';
import { useVoucherLock } from '../../../../components/gating/PlanGate';
import AiAnalysisProgress from '../../../Vouchers/sections/CreateVoucher/sections/AiVoucher/AiAnalysisProgress';
import FeatureUpdateModal from '../../../../components/modals/FeatureUpdateModal';
import VoucherGeneratorCard from './sections/VoucherGeneratorCard';
import GetSetUpCard from './sections/GetSetUpCard/GetSetUpCard';

const MAX_TOTAL_FILES = 30;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const DashboardGetStarted = ({
  stats,
  agentProfile,
  recentVouchers = [],
  onStartTour,
  isTourActive,
  tourStep = 0
}) => {
  const navigate = useNavigate();
  const { userData } = useAgent();
  const {
    analysisState,
    startVoucherAnalysis,
    resetAnalysis,
    setAnalysisMinimized,
    agentUpdatesEnabled = true
  } = useDashboard();
  const { isLocked: isVoucherLocked } = useVoucherLock();

  // Updates Modal State
  const [isUpdatesModalOpen, setIsUpdatesModalOpen] = useState(false);
  const [updatesModalInitialSlide, setUpdatesModalInitialSlide] = useState(0);

  // Upload States for 3 Document Boxes
  const [hotelFiles, setHotelFiles] = useState([]);
  const [travelFiles, setTravelFiles] = useState([]);
  const [itineraryFiles, setItineraryFiles] = useState([]);

  const [isHotelDragging, setIsHotelDragging] = useState(false);
  const [isTravelDragging, setIsTravelDragging] = useState(false);
  const [isItineraryDragging, setIsItineraryDragging] = useState(false);

  const hotelInputRef = useRef(null);
  const travelInputRef = useRef(null);
  const itineraryInputRef = useRef(null);

  // Simulated uploaded files during tour
  const hasTourSimulatedFiles = isTourActive && tourStep >= 1;

  const effectiveHotelFiles =
    hotelFiles.length > 0
      ? hotelFiles
      : hasTourSimulatedFiles
      ? [{ name: 'Pearl_Palace_Jaipur_Booking.pdf' }]
      : [];

  const effectiveTravelFiles =
    travelFiles.length > 0
      ? travelFiles
      : hasTourSimulatedFiles
      ? [{ name: 'IndiGo_Flight_6E214_Ticket.pdf' }]
      : [];

  const effectiveItineraryFiles =
    itineraryFiles.length > 0
      ? itineraryFiles
      : hasTourSimulatedFiles
      ? [{ name: 'Jaipur_4Day_Itinerary_Schedule.pdf' }]
      : [];

  // Handle Handoff to Manual Editor on Extraction Complete
  useEffect(() => {
    if (analysisState?.result && analysisState?.analysisType === 'initial') {
      toast.success('Analysis complete! Opening editor...');
      const extractedData = analysisState.result;
      setHotelFiles([]);
      setTravelFiles([]);
      setItineraryFiles([]);
      resetAnalysis();
      navigate('/create-voucher', {
        state: {
          activeTab: 'manual',
          initialData: extractedData
        }
      });
    }
  }, [analysisState?.result, analysisState?.analysisType, navigate, resetAnalysis]);

  // File Validation Helper
  const validateFiles = (incomingFiles, currentZoneFiles, otherZonesFiles = []) => {
    const totalOtherCount = otherZonesFiles.reduce((acc, curr) => acc + curr.length, 0);
    const totalCount = currentZoneFiles.length + totalOtherCount;

    if (totalCount >= MAX_TOTAL_FILES) {
      toast.error(`Maximum limit of ${MAX_TOTAL_FILES} files reached.`);
      return [];
    }

    const validTypes = incomingFiles.filter(
      (file) => file.type === 'application/pdf' || file.type.startsWith('image/')
    );

    if (validTypes.length < incomingFiles.length) {
      toast.error('Some files were skipped. Only PDFs and Images are allowed.');
    }

    const sizeChecked = validTypes.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} exceeds 50MB limit.`);
        return false;
      }
      return true;
    });

    const remainingSlots = MAX_TOTAL_FILES - totalCount;
    if (sizeChecked.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more files can be added.`);
      return sizeChecked.slice(0, remainingSlots);
    }

    return sizeChecked;
  };

  const createDropHandler = (setter, currentFiles, otherZones, dragSetter) => (e) => {
    e.preventDefault();
    dragSetter(false);
    if (isVoucherLocked) {
      navigate('/subscription');
      toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
      return;
    }
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = validateFiles(droppedFiles, currentFiles, otherZones);

    if (validFiles.length > 0) {
      setter((prev) => {
        const existingNames = new Set(prev.map((f) => f.name + f.size));
        const newFiles = validFiles.filter((f) => !existingNames.has(f.name + f.size));
        return [...prev, ...newFiles];
      });
    }
  };

  const handleFileInputChange = (setter, currentFiles, otherZones) => (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = validateFiles(selectedFiles, currentFiles, otherZones);

    if (validFiles.length > 0) {
      setter((prev) => {
        const existingNames = new Set(prev.map((f) => f.name + f.size));
        const newFiles = validFiles.filter((f) => !existingNames.has(f.name + f.size));
        return [...prev, ...newFiles];
      });
    }
    e.target.value = '';
  };

  const handleGenerate = () => {
    if (isVoucherLocked) {
      navigate('/subscription');
      toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
      return;
    }
    if (hotelFiles.length === 0 && travelFiles.length === 0 && itineraryFiles.length === 0) {
      if (hasTourSimulatedFiles) {
        navigate('/create-voucher', {
          state: {
            isTourSample: true
          }
        });
        return;
      }
      toast.error('Please upload at least a Hotel Confirmation to start generating your voucher.');
      return;
    }

    const docFiles = [...hotelFiles, ...travelFiles];
    startVoucherAnalysis(docFiles, itineraryFiles);
  };

  // Right card visibility check
  const hasCompletedProfiling = Boolean(
    agentProfile?.profiling_answers && agentProfile.profiling_answers.length > 0
  );
  const showProfileTab = !hasCompletedProfiling;
  const showUpdatesTab = Boolean(agentUpdatesEnabled);
  const hasAnyRightTab = true; // Setup or Profile or Updates is always present or computed inside GetSetUpCard

  // Responsive Layout
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('sidebar_collapsed') === 'true'
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    const handleSidebarChange = () => {
      setIsSidebarCollapsed(localStorage.getItem('sidebar_collapsed') === 'true');
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('sidebar_state_changed', handleSidebarChange);
    window.addEventListener('storage', handleSidebarChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('sidebar_state_changed', handleSidebarChange);
      window.removeEventListener('storage', handleSidebarChange);
    };
  }, []);

  const isSideBySide = hasAnyRightTab && (isSidebarCollapsed ? windowWidth > 1250 : windowWidth > 1366);

  const leftCardRef = useRef(null);
  const [leftCardHeight, setLeftCardHeight] = useState(null);

  useLayoutEffect(() => {
    const el = leftCardRef.current;
    if (!el) return;

    const updateHeight = () => {
      if (isSideBySide) {
        setLeftCardHeight(el.offsetHeight);
      } else {
        setLeftCardHeight(null);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeight();
    });
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isSideBySide, hasAnyRightTab, hotelFiles.length, travelFiles.length, itineraryFiles.length]);

  return (
    <div className="w-full">
      {/* Responsive Layout: Side-by-side above threshold, stacked in full width below threshold */}
      <div
        className={`w-full flex ${
          isSideBySide ? 'flex-row items-start' : 'flex-col'
        } ${hasAnyRightTab ? 'gap-5 lg:gap-6' : ''}`}
      >
        {/* Left Sub-Section: Voucher Generator */}
        <VoucherGeneratorCard
          leftCardRef={leftCardRef}
          isSideBySide={isSideBySide}
          hasAnyRightTab={hasAnyRightTab}
          hotelFiles={hotelFiles}
          setHotelFiles={setHotelFiles}
          travelFiles={travelFiles}
          setTravelFiles={setTravelFiles}
          itineraryFiles={itineraryFiles}
          setItineraryFiles={setItineraryFiles}
          isHotelDragging={isHotelDragging}
          setIsHotelDragging={setIsHotelDragging}
          isTravelDragging={isTravelDragging}
          setIsTravelDragging={setIsTravelDragging}
          isItineraryDragging={isItineraryDragging}
          setIsItineraryDragging={setIsItineraryDragging}
          hotelInputRef={hotelInputRef}
          travelInputRef={travelInputRef}
          itineraryInputRef={itineraryInputRef}
          effectiveHotelFiles={effectiveHotelFiles}
          effectiveTravelFiles={effectiveTravelFiles}
          effectiveItineraryFiles={effectiveItineraryFiles}
          createDropHandler={createDropHandler}
          handleFileInputChange={handleFileInputChange}
          handleGenerate={handleGenerate}
          isTourActive={isTourActive}
          onStartTour={onStartTour}
          isVoucherLocked={isVoucherLocked}
          analysisState={analysisState}
        />

        {/* Right Sub-Section: Get Set Up / Business Profile / What's New */}
        {hasAnyRightTab && (
          <GetSetUpCard
            agentProfile={agentProfile}
            userData={userData}
            stats={stats}
            recentVouchers={recentVouchers}
            hotelInputRef={hotelInputRef}
            isSideBySide={isSideBySide}
            leftCardHeight={leftCardHeight}
            agentUpdatesEnabled={agentUpdatesEnabled}
            isVoucherLocked={isVoucherLocked}
            onOpenUpdatesModal={(slideIndex) => {
              setUpdatesModalInitialSlide(slideIndex || 0);
              setIsUpdatesModalOpen(true);
            }}
          />
        )}
      </div>

      {/* Persistent Analysis Loader Modal */}
      <AiAnalysisProgress
        analysisState={analysisState}
        setAnalysisMinimized={setAnalysisMinimized}
        resetGlobalAnalysis={resetAnalysis}
      />

      {/* Feature Update Interactive Walkthrough Modal */}
      <FeatureUpdateModal
        isOpen={isUpdatesModalOpen}
        initialSlide={updatesModalInitialSlide}
        onClose={() => setIsUpdatesModalOpen(false)}
      />
    </div>
  );
};

export default DashboardGetStarted;
